import type { JSX, ReactNode } from 'react';
import {
  Card,
  IconChip,
  IconBellNotification,
  IconCalendarAdd,
  IconCog,
  IconDownload,
  IconHome1,
  IconOfficeBuilding2,
  IconStoreFactory,
  IconStore2,
  IconStrategyTasks,
  IconUserIdentifierCard,
  IconWarehouse1,
} from '@taki/ui';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface AppStoreScreenProps {}

/** Un template d'industrie du catalogue v0 (en dur, pas de backend). */
interface IndustryTemplate {
  id: string;
  /** Nom du template. En anglais, comme les noms d'apps du registre. */
  name: string;
  /** Le metier en trois mots, sous le nom. */
  tagline: string;
  /** Compteur d'installations, editorial en v0 (aucun backend derriere). */
  installs: string;
  icon: ReactNode;
}

/**
 * Catalogue v0 (en dur). L'ordre est editorial. Chaque template se distingue par
 * son ICONE, jamais par une couleur : toutes les pastilles sont neutres.
 *
 * Les icones viennent du set Streamline de @taki/ui, choisies pour correspondre
 * a celles de la maquette (Spot Motion E2), aucune n'est dessinee a la main.
 */
const TEMPLATES: readonly IndustryTemplate[] = [
  {
    id: 'retail',
    name: 'Retail',
    tagline: 'Caisse et stock',
    installs: '1.9k',
    icon: <IconStore2 size={22} />,
  },
  {
    id: 'logistics',
    name: 'Logistics',
    tagline: 'Flotte et livraisons',
    installs: '940',
    icon: <IconWarehouse1 size={22} />,
  },
  {
    id: 'real-estate',
    name: 'Real Estate',
    tagline: 'Annonces et visites',
    installs: '3.8k',
    icon: <IconOfficeBuilding2 size={22} />,
  },
  {
    id: 'hotels',
    name: 'Hotels',
    tagline: 'Reservations et chambres',
    installs: '1.3k',
    icon: <IconBellNotification size={22} />,
  },
  {
    id: 'manufacturing',
    name: 'Manufacturing',
    tagline: 'Commandes et production',
    installs: '720',
    icon: <IconStoreFactory size={22} />,
  },
  {
    id: 'field-service',
    name: 'Field Service',
    tagline: 'Interventions et planning',
    installs: '1.1k',
    icon: <IconStrategyTasks size={22} />,
  },
  {
    id: 'property-management',
    name: 'Property Mgmt',
    tagline: 'Locataires et baux',
    installs: '860',
    icon: <IconHome1 size={22} />,
  },
  {
    id: 'events',
    name: 'Events',
    tagline: 'Planning et reservations',
    installs: '1.5k',
    icon: <IconCalendarAdd size={22} />,
  },
  {
    id: 'workshop',
    name: 'Workshop',
    tagline: 'Reparations et pieces',
    installs: '540',
    icon: <IconCog size={22} />,
  },
  {
    id: 'agencies',
    name: 'Agencies',
    tagline: 'Clients et projets',
    installs: '2.1k',
    icon: <IconUserIdentifierCard size={22} />,
  },
];

/** Carte large d'un template : pastille + nom + metier + compteur. */
function TemplateCard({ template }: { template: IndustryTemplate }): JSX.Element {
  return (
    <Card interactive className="flex items-center gap-[1.125rem] px-[1.375rem] py-[0.9375rem]">
      <IconChip size={44}>{template.icon}</IconChip>

      <div className="min-w-0 flex-1">
        <div className="truncate text-[1.125rem] font-semibold tracking-[-0.01em] text-ink-900">
          {template.name}
        </div>
        <div className="mt-[0.1875rem] truncate text-[0.84375rem] text-ink-400">{template.tagline}</div>
      </div>

      <div className="flex shrink-0 items-center gap-[0.4375rem] text-ink-500">
        <IconDownload size={14} />
        <span className="whitespace-nowrap text-[0.8125rem] font-medium">
          {template.installs} installations
        </span>
      </div>
    </Card>
  );
}

/**
 * Ecran "App Store" : on ne part pas d'une app, on part d'un TEMPLATE METIER
 * que le client fait ensuite sien (maquette Spot Motion E2).
 *
 * Catalogue v0 en dur, sans backend : comme aujourd'hui, l'App Store n'installe
 * rien. La pop-up de confirmation (maquette E3) et le vrai flux d'installation
 * viendront ensuite.
 *
 * L'overline "APP STORE" et le chip utilisateur sont rendus par le shell.
 * UI 100% via @taki/ui, tokens uniquement, zero hex en dur.
 */
export function AppStoreScreen(_props: AppStoreScreenProps): JSX.Element {
  return (
    <div className="flex flex-col">
      <h1 className="text-[1.6875rem] font-bold leading-tight tracking-[-0.025em] text-ink-900">
        Templates metier
      </h1>
      <p className="mt-[0.4375rem] max-w-[27.5rem] text-[0.9375rem] leading-normal text-ink-500">
        Demarre ton app sur-mesure depuis un template metier, puis fais-la tienne.
      </p>

      <div className="mt-[1.375rem] grid grid-cols-2 content-start gap-4">
        {TEMPLATES.map((template) => (
          <TemplateCard key={template.id} template={template} />
        ))}
      </div>
    </div>
  );
}
